import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private isInitialized = false;

  constructor() {
    this.initializeAppInsights();
  }

  private initializeAppInsights(): void {
    try {
      // Only initialize if instrumentation key is provided
      const instrumentationKey = environment.appInsightsInstrumentationKey;
      
      if (!instrumentationKey || instrumentationKey.startsWith('#{')) {
        console.warn('Application Insights: No valid instrumentation key provided. Telemetry will not be sent.');
        return;
      }

      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          disableFetchTracking: false,
          disableAjaxTracking: false,
          // Disable telemetry if it fails to prevent app disruption
          disableExceptionTracking: false,
          autoTrackPageVisitTime: true,
          // Add custom error handling to catch and suppress AppInsights errors
          loggingLevelTelemetry: 2, // Warning level
          loggingLevelConsole: 1, // Error level only
        }
      });

      this.appInsights.loadAppInsights();
      
      // Add telemetry initializer to tag all telemetry with source identifier
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.data) {
          envelope.data['source'] = 'PropertyMDM_ClientLog';
        }
        if (envelope.baseData) {
          if (!envelope.baseData['properties']) {
            envelope.baseData['properties'] = {};
          }
          envelope.baseData['properties']['source'] = 'PropertyMDM_ClientLog';
        }
      });
      
      // Set authenticated user context if available
      this.setAuthenticatedUserContext();
      
      this.isInitialized = true;
      console.log('Application Insights initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
      console.warn('Application will continue without telemetry');
      this.appInsights = null;
      this.isInitialized = false;
    }
  }

  /**
   * Track a page view
   */
  trackPageView(name?: string, uri?: string): void {
    this.safeExecute(() => {
      this.appInsights?.trackPageView({ name, uri });
    });
  }

  /**
   * Track an exception
   */
  trackException(error: Error, severityLevel?: number): void {
    this.safeExecute(() => {
      this.appInsights?.trackException({ 
        exception: error,
        severityLevel 
      });
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    // Only track events if enabled in environment configuration
    if (!environment.appInsightsEnableTrackEvents) {
      return;
    }
    
    this.safeExecute(() => {
      this.appInsights?.trackEvent({ name }, properties);
    });
  }

  /**
   * Track a custom metric
   */
  trackMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    this.safeExecute(() => {
      this.appInsights?.trackMetric({ name, average }, properties);
    });
  }

  /**
   * Track a trace/log message
   */
  trackTrace(message: string, properties?: Record<string, unknown>): void {
    this.safeExecute(() => {
      this.appInsights?.trackTrace({ message }, properties);
    });
  }

  /**
   * Set authenticated user context
   */
  setAuthenticatedUserContext(userId?: string, accountId?: string): void {
    this.safeExecute(() => {
      if (userId) {
        this.appInsights?.setAuthenticatedUserContext(userId, accountId);
      } else {
        this.appInsights?.clearAuthenticatedUserContext();
      }
    });
  }

  /**
   * Clear authenticated user context
   */
  clearAuthenticatedUserContext(): void {
    this.safeExecute(() => {
      this.appInsights?.clearAuthenticatedUserContext();
    });
  }

  /**
   * Check if AppInsights is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Safely execute AppInsights operations with error handling
   * to prevent AppInsights failures from breaking the app
   */
  private safeExecute(operation: () => void): void {
    if (!this.isInitialized || !this.appInsights) {
      return;
    }

    try {
      operation();
    } catch (error) {
      console.error('Application Insights operation failed:', error);
      // Swallow the error to prevent it from propagating to the app
    }
  }
}
